import { Button, Tabs } from "@jhool-io/fe-components";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { cn, convertUrlSearchParamsToObject } from "../../../../utils/helpers";
import InvoiceNotes from "../../../billing/components/InvoiceNotes/InvoiceNotes";
import BackLink from "../../../../components/BackLink/BackLink";
import HarmonizedView from "../../../../shared-ui/HarmonizedView/HarmonizedView";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import ClaimsWrapper from "../../../claims/components/ClaimsWrapper/ClaimsWrapper";
import PaymentTabWrapper from "../../../billing/components/PaymentTabWrapper/PaymentTabWrapper";
import RemitsWrapper from "../RemitsWrapper/RemitsWrapper";
import { useFetchRemitClaims } from "../../hooks/remits.queries";
import NoteItemsWrapper from "../../../notes/components/NoteItemsWrapper/NoteItemsWrapper";
import { IAppCustomDates } from "../../../../utils/types";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import ChevronLeftIcon from "../../../../components/Icons/ChevronLeft";
import ChevronRightIcon from "../../../../components/Icons/ChevronRight";

export default function ViewRemit() {
    const [searchParams, setSearchParams] = useSearchParams();
    const params = useParams();

    const { clientId, noteId, remitId } = params;

    const navigate = useNavigate();

    const remitClaimsMeta = localStorage.getItem("remitClaimsMeta")
        ? JSON.parse(localStorage.getItem("remitClaimsMeta") || "{}")
        : null;

    const backUrl = remitClaimsMeta ? remitClaimsMeta.remitClaimsBackUrl : "";

    const remitClaimsParamsObj =
        remitClaimsMeta && remitClaimsMeta?.remitClaimsParams
            ? convertUrlSearchParamsToObject(remitClaimsMeta?.remitClaimsParams)
            : null;

    const remitIdForRemitClaims = remitClaimsMeta?.remitId;

    const { data } = useFetchSessionNote(
        clientId as string,
        noteId as string,
        Boolean(clientId) && Boolean(noteId)
    );

    const remitClaims = useFetchRemitClaims(
        {
            batch_remit_id: remitIdForRemitClaims,
            search_string: remitClaimsParamsObj?.search_string || "",
            claim_status: remitClaimsParamsObj?.claim_status || "",
            page: remitClaimsParamsObj?.page
                ? Number(remitClaimsParamsObj?.page)
                : 1,
            limit: remitClaimsParamsObj?.limit
                ? Number(remitClaimsParamsObj?.limit)
                : 300,
            sort_attribute: remitClaimsParamsObj?.sort_attr,
            sort_order: remitClaimsParamsObj?.sort_order,
            custom_date: remitClaimsParamsObj?.date_range
                ? (remitClaimsParamsObj?.date_range as IAppCustomDates)
                : undefined,
            from_date: remitClaimsParamsObj?.from_date,
            to_date: remitClaimsParamsObj?.to_date,
        },
        Boolean(remitIdForRemitClaims)
    );

    const arrayOfRemitClaimsToNavigateWith = remitClaims?.data?.data || [];

    const hideNaviagtionButtons =
        arrayOfRemitClaimsToNavigateWith.length < 2 ||
        remitClaims?.error ||
        !remitIdForRemitClaims;

    const getCurrentIndex = () => {
        const getCurrent = arrayOfRemitClaimsToNavigateWith.findIndex(
            (claim) => claim.remittance_id === remitId
        );

        return getCurrent;
    };

    const handlePrevButtonClick = () => {
        const currentIndex = getCurrentIndex();
        if (currentIndex === 0) return;

        const {
            remittance_id: remittanceId,
            claim_id: claimId,
            client,
            note_id: remitNoteId,
        } = arrayOfRemitClaimsToNavigateWith[currentIndex - 1];

        navigate(
            `/remits/${remittanceId}/${claimId}/${client.client_id}/${remitNoteId}?tab=remit&remit_in_view=${remittanceId}`
        );
    };

    const handleNextButtonClick = () => {
        const currentIndex = getCurrentIndex();
        if (currentIndex === arrayOfRemitClaimsToNavigateWith.length - 1) {
            return;
        }

        const {
            remittance_id: remittanceId,
            claim_id: claimId,
            client,
            note_id: remitNoteId,
        } = arrayOfRemitClaimsToNavigateWith[currentIndex + 1];

        navigate(
            `/remits/${remittanceId}/${claimId}/${client.client_id}/${remitNoteId}?tab=remit&remit_in_view=${remittanceId}`
        );
    };

    const handleBackButtonClick = () => {
        if (backUrl) {
            navigate(backUrl);
            localStorage.removeItem("remitClaimsMeta");
        } else {
            navigate(-1);
        }
    };

    const handleSetTabInUrl = (tab: string) => {
        searchParams.set("tab", tab);
        setSearchParams(searchParams);
    };

    const tabItems = [
        {
            key: "notes",
            label: "Session Note",
            children: (
                <div className="mt-24">
                    <NoteItemsWrapper />
                </div>
            ),
            onClick: () => handleSetTabInUrl("note"),
        },
        {
            key: "claims",
            label: "Claims",
            children: (
                <div className="mt-24">
                    <ClaimsWrapper />
                </div>
            ),
            onClick: () => handleSetTabInUrl("claims"),
            disabled: !data?.data?.claim_id,
        },
        {
            key: "payments",
            label: "Payments History",
            children: (
                <div className="mt-24">
                    <PaymentTabWrapper />
                </div>
            ),
            onClick: () => handleSetTabInUrl("payments"),
            disabled: data?.data.is_archived && !data?.data.invoice_id,
        },
        {
            key: "remit",
            label: "Remits",
            children: (
                <div className="mt-24">
                    <RemitsWrapper />
                </div>
            ),
            onClick: () => handleSetTabInUrl("remit"),
        },
    ];

    return (
        <div>
            <div className="mb-32 flex justify-between items-center gap-x-8">
                <BackLink
                    onClick={handleBackButtonClick}
                    title="Back to remit claims page"
                />
                {!hideNaviagtionButtons ? (
                    <div>
                        {remitClaims?.isLoading ? (
                            <div className="flex items-center gap-x-8">
                                <Skeleton width="200px" height={20} />
                            </div>
                        ) : null}
                        {remitClaims.isSuccess ? (
                            <div className="flex items-center gap-x-24 text-sm font-medium">
                                <Button
                                    variant="normal"
                                    size="auto"
                                    disabled={
                                        !getCurrentIndex() ||
                                        getCurrentIndex() <= 0
                                    }
                                    onClick={handlePrevButtonClick}
                                    className="gap-x-4 underline"
                                >
                                    <ChevronLeftIcon /> Prev
                                </Button>
                                <span className="flex items-center gap-x-4">
                                    <span>{(getCurrentIndex() || 0) + 1}</span>
                                    <span className="text-gray font-normal">
                                        OF
                                    </span>
                                    <span>
                                        {
                                            arrayOfRemitClaimsToNavigateWith.length
                                        }
                                    </span>
                                </span>
                                <Button
                                    variant="normal"
                                    size="auto"
                                    disabled={
                                        getCurrentIndex() >=
                                        arrayOfRemitClaimsToNavigateWith.length -
                                            1
                                    }
                                    onClick={handleNextButtonClick}
                                    className="gap-x-4 underline"
                                >
                                    Next
                                    <ChevronRightIcon />
                                </Button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
            <div className="flex items-stretch max-[1100px]:flex-col gap-x-8">
                <div className="w-[70%] max-[1100px]:w-full max-[1100px]:mb-10">
                    <HarmonizedView />
                </div>
                <div className=" w-[30%] max-[1100px]:w-full max-[1100px]:ml-0 border border-strokelight rounded-r8">
                    <InvoiceNotes invoiceId={data?.data.invoice_id as string} />
                </div>
            </div>

            <div className="pt-24">
                <Tabs
                    items={tabItems}
                    className={cn("!w-[100%] !mb-0 !items-end")}
                    defaultActiveKey={
                        searchParams.get("tab") || tabItems[0].key
                    }
                    size="small"
                />
            </div>
        </div>
    );
}
